import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/types/enums/recipient-status';
import { DocumentPageType } from '@getaccept/lib-shared/src/enums/document-page-type';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { DocumentRecipientRevision } from '@getaccept/lib-shared/src/types/document-revision';
import type { PageElement } from '../../signing/types/page-element';
import { TrackingConstants } from '../constants/tracking';
import type { TrackVisitTimeDto } from '../../api/tracking/types/track-visit-time-dto';

export class TrackingHelper {
  private static getContentTrackingPercent(page: PageElement): number {
    const { type } = page;
    switch (type) {
      case DocumentPageType.Editor:
      case DocumentPageType.DocumentContent:
        return 10;
      case DocumentPageType.Video:
      case DocumentPageType.ContentLink:
        return 80;
      default:
        return 50;
    }
  }

  static allowedTrackingRecipientStatus(status: RecipientStatus): boolean {
    const forbiddenStatuses: RecipientStatus[] = [
      RecipientStatus.Signed,
      RecipientStatus.Approved,
      RecipientStatus.Rejected,
    ];
    return !forbiddenStatuses.includes(status);
  }

  static shouldSendViewedPush(
    documentRecipients: SigningPageRecipient[],
    recipientId: string
  ): boolean {
    const recipient: SigningPageRecipient = documentRecipients.find(
      (recipient: SigningPageRecipient) => recipient.id === recipientId
    );
    return [RecipientStatus.Added, RecipientStatus.Sent].includes(recipient?.status);
  }

  static isPageInFocus(page: PageElement): boolean {
    const positionTop: number =
      page.element.getBoundingClientRect().top - TrackingConstants.topBarHeight;
    const positionBottom: number =
      page.element.getBoundingClientRect().bottom - TrackingConstants.topBarHeight;

    const pageHeight: number = page.element.clientHeight;
    const percent: number = pageHeight / this.getContentTrackingPercent(page);

    return positionBottom > percent || positionTop > percent;
  }

  static getVisitTimesPayload(
    visitTimes: {
      [pageId: string]: { pageNum: number; pageTime: number };
    },
    latestSealedRevision: DocumentRecipientRevision
  ): TrackVisitTimeDto[] {
    const payload = Object.keys(visitTimes).map((id: string) => ({
      pageId: id,
      pageTime: visitTimes[id].pageTime * 1000,
      pageNum: visitTimes[id].pageNum,
      revision: latestSealedRevision?.revision,
    }));
    return payload.filter((visitTime: TrackVisitTimeDto) => !!visitTime.pageTime);
  }
}
